<template>
  <ModalRoot v-model="isOpen">
    <template #body="{ close }">
      <ModalBody
        :is-open="isOpen"
        hide-close-button
        mobile-full-screen
        class="!p-0 !bg-bg overflow-hidden w-full md:w-[500px] rounded-0 md:rounded-[35px]"
        @close="close"
      >
        <div
          class="p-6 bg-fill-brand-secondary backdrop-blur-[100px] flex flex-col gap-6 h-screen md:h-auto"
        >
          <!-- Header -->
          <div class="h-10 justify-between items-center flex w-full">
            <h2 class="text-xl text-text font-serif">
              {{ $t("channel.leaderboard.rewardUser.title") }}
            </h2>
            <div
              class="w-10 h-10 bg-fill-brand-secondary rounded-full backdrop-blur-[35px] flex justify-center items-center cursor-pointer"
              @click="close"
            >
              <Icon icon="mdi:close" class="size-6 text-white" />
            </div>
          </div>

          <!-- Content -->
          <div
            class="flex-col justify-start items-start gap-4 inline-flex overflow-y-auto hide-scrollbar"
          >
            <!-- Search Input -->
            <div v-if="!selectedUser" class="w-full">
              <TextInput
                v-model="searchQuery"
                :label="$t('channel.leaderboard.rewardUser.searchLabel')"
                :placeholder="
                  $t('channel.leaderboard.rewardUser.searchPlaceholder')
                "
                class="w-full"
              >
                <template #prefix>
                  <Icon
                    icon="material-symbols:search"
                    class="size-5 text-white/60"
                  />
                </template>
              </TextInput>
            </div>

            <!-- Users List -->
            <div class="flex flex-col gap-2 w-full overflow-y-auto">
              <div
                v-for="user in users"
                :key="user.id"
                class="flex items-center gap-3 py-2.5 px-4 px bg-fill-brand-secondary rounded-xl cursor-pointer hover:bg-fill-brand-secondary-hover transition-colors w-full overflow-x-clip"
                @click="onSelectUser(user)"
              >
                <Avatar :user="user" :alt="user.username" />
                <div class="flex flex-col">
                  <div
                    class="text-white text-sm font-medium font-sans leading-[18px]"
                  >
                    {{ user.username }}
                  </div>
                  <div
                    class="text-white/80 text-sm font-medium font-sans leading-[18px]"
                  >
                    {{ user.email }}
                  </div>
                </div>
              </div>
            </div>

            <RewardUserActionSelect
              v-if="selectedUser"
              :selected-user="selectedUser"
              @reset="selectedUser = null"
            />
          </div>
        </div>
      </ModalBody>
    </template>
  </ModalRoot>
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";
const _FindByUserResultFragment = gql(/* GraphQL */ `
  fragment FindByUserResult on UserForAdmin {
    id
    username
    email
    avatar
  }
`);
</script>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import ModalBody from "@/components/ui/modal/modal-body.vue";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import TextInput from "@/components/ui/form/text-input.vue";
import RewardUserActionSelect from "./reward-user-action-select.vue";
import Avatar from "@/components/me/avatar.vue";

import { ref, computed } from "vue";
import { useQuery } from "@vue/apollo-composable";

import type { ResultOf } from "@graphql-typed-document-node/core";

const FindByUserDocument = gql(/* GraphQL */ `
  query FindUsersByQuery($query: String!) {
    findUsersByQuery(query: $query) {
      ...FindByUserResult
    }
  }
`);

type FindByUser = ResultOf<typeof _FindByUserResultFragment>;

const isOpen = defineModel<boolean>("isOpen", { required: true });
const searchQuery = ref("");
const selectedUser = ref<FindByUser | null>(null);

const { result: usersResult } = useQuery(
  FindByUserDocument,
  computed(() => ({
    query: searchQuery.value,
  })),
  {
    debounce: 250,
  },
);

const users = computed(
  () =>
    (selectedUser.value
      ? [selectedUser.value]
      : usersResult.value?.findUsersByQuery || []) as FindByUser[],
);

const onSelectUser = (user: FindByUser) => {
  if (selectedUser.value?.id === user.id) {
    selectedUser.value = null;
  } else {
    selectedUser.value = user;
  }
};
</script>
